import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Copy, Zap, Users, Wallet, MapPin, Recycle, ShieldCheck, Sparkles, ChevronDown, Wrench } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { homeFor } from "../components/ProtectedRoute";
import { B2B_REG } from "../components/BlurredPrice";
import useSeo from "../hooks/useSeo";
import SopralluogoForm from "../components/SopralluogoForm";

const KEG = "https://images.unsplash.com/photo-1760282162326-0460fb0e2c1a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const DRINK = "https://images.unsplash.com/photo-1564090654281-eb1db56d15fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const INOX = "https://images.unsplash.com/photo-1567696911980-2eed69a46042?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const fadeUp = { hidden: { opacity: 0, y: 28 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }) };
const VIEWPORT = { once: true, margin: "-40px" };
const EXPAND = { initial: { opacity: 0, height: 0 }, animate: { opacity: 1, height: "auto" }, exit: { opacity: 0, height: 0 }, transition: { duration: 0.3 } };
const MACHINE_TYPES = [
  { key: "spillatore", label: "Spillatori", text: "Impianti refrigerati inox per fusti Cornelius 19L", image: KEG },
  { key: "granitore", label: "Granitori", text: "Granitori professionali per preparati e succhi 100% frutta", image: DRINK },
];
const BENEFITS = [
  { icon: Copy, title: "Drink sempre uguali", text: "Ricetta standardizzata: ogni cocktail identico al precedente, senza variazioni tra un turno e l'altro." },
  { icon: Zap, title: "Mescita immediata", text: "Servi in pochi secondi anche nei momenti di picco, con la semplicità di una spillatura." },
  { icon: Users, title: "Barman più liberi", text: "Lo staff si concentra sui drink più complessi mentre i grandi classici scorrono dal fusto." },
  { icon: Wallet, title: "Risparmio per la struttura", text: "Alberghi e ristoranti servono cocktail di qualità anche senza un barman dedicato." },
  { icon: MapPin, title: "Installabile in ogni locale", text: "Dalle barche ai chioschi, dai lidi alle discoteche: bastano l'impianto e i fusti." },
  { icon: Recycle, title: "Vuoto a rendere", text: "Fusti Cornelius 19L riutilizzabili: fino a -85% di vetro e plastica. Cauzione fusto accreditata al reso." },
  { icon: ShieldCheck, title: "Acciaio inox alimentare", text: "I nostri fusti sono in acciaio inox: conservano il prodotto in modo perfetto, senza alterazioni di gusto." },
  { icon: Sparkles, title: "Autocarbonazione integrata", text: "Un sistema di autocarbonazione studiato nel fusto: i drink non sono mai sgasati o poco gasati, ma sempre perfetti." },
];
const DEFAULT = {
  eyebrow: "Impianti in comodato d'uso", title: "Spillatori e granitori, senza pensieri",
  text: "Diamo in comodato d'uso spillatori refrigerati in acciaio inox per i fusti Cornelius 19L e granitori professionali per i nostri preparati di frutta.",
  bullets: ["Formula a canone zero legata ai consumi", "Assistenza e manutenzione incluse", "Ammortamento calcolato sul volume di fusti"], image1: KEG, image2: INOX,
};

function Machines({ machines, target }) {
  const [openType, setOpenType] = useState(null);
  const types = useMemo(() => MACHINE_TYPES.filter((t) => machines.some((m) => m.type === t.key)).map((t) => ({ ...t, items: machines.filter((m) => m.type === t.key) })), [machines]);
  const openItems = useMemo(() => machines.filter((m) => m.type === openType), [machines, openType]);
  if (machines.length === 0) return null;
  return (
    <div className="mt-20" data-testid="comodato-machines">
      <div className="text-center mb-10">
        <div className="eyebrow text-[#C2593F] mb-3">I nostri impianti</div>
        <h2 className="font-serif font-semibold text-2xl lg:text-3xl text-[#0B2A45]">Le macchine che diamo in comodato</h2>
        <p className="text-sm text-stone-500 mt-2">Tocca una categoria per vedere i modelli disponibili</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {types.map((t, i) => {
          const { items } = t;
          const open = openType === t.key;
          return (
            <motion.button type="button" key={t.key} onClick={() => setOpenType(open ? null : t.key)} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={VIEWPORT}
              className={`text-left bg-white rounded-2xl overflow-hidden border transition-[transform,box-shadow,border-color] duration-300 hover:shadow-xl hover:-translate-y-1 ${open ? "border-[#0F4C81] shadow-xl" : "border-stone-200"}`}
              data-testid={`comodato-machine-type-${t.key}`} aria-expanded={open}>
              <div className="aspect-[4/3] bg-stone-100 relative">
                <img src={items.find((m) => m.image_url)?.image_url || t.image} alt={t.label} className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 text-[0.65rem] uppercase tracking-wider font-semibold bg-white/90 text-[#0F4C81] px-2 py-1 rounded-full">{items.length} {items.length === 1 ? "modello" : "modelli"}</span>
              </div>
              <div className="p-5 flex items-center justify-between gap-3">
                <div><h3 className="font-serif text-xl font-semibold text-[#0B2A45]">{t.label}</h3><p className="text-sm text-stone-500 mt-1">{t.text}</p></div>
                <ChevronDown className={`w-5 h-5 text-[#0F4C81] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
              </div>
            </motion.button>
          );
        })}
      </div>
      <AnimatePresence>
        {openType && (
          <motion.div key={openType} {...EXPAND} className="overflow-hidden" data-testid={`comodato-machines-list-${openType}`}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {openItems.map((m) => (
                <div key={m.id} className="bg-white rounded-2xl overflow-hidden border border-stone-200" data-testid={`comodato-machine-card-${m.id}`}>
                  <Link to={target} className="block aspect-[4/3] bg-stone-100" aria-label={m.name}>
                    {m.image_url ? <img src={m.image_url} alt={m.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-stone-300"><Wrench className="w-10 h-10" /></div>}
                  </Link>
                  <div className="p-5">
                    <h3 className="font-serif text-lg font-semibold text-[#0B2A45]">{m.name}</h3>
                    {m.description ? <p className="text-sm text-stone-500 mt-2 leading-relaxed">{m.description}</p> : null}
                    <Link to={target} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C2593F] mt-4 hover:gap-2 transition-all" data-testid={`comodato-machine-cta-${m.id}`}>Richiedi in comodato <ArrowRight className="w-4 h-4" /></Link>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Comodato() {
  const { user } = useAuth();
  const target = user ? homeFor(user.role) : B2B_REG;
  const [machines, setMachines] = useState([]);
  const [c, setC] = useState(DEFAULT);
  useEffect(() => {
    api.get("/machines").then(({ data }) => setMachines((data || []).filter((m) => m.active !== false))).catch(() => {});
    api.get("/content").then(({ data }) => setC({ ...DEFAULT, ...(data?.comodato || {}) })).catch(() => {});
  }, []);
  useSeo({ path: "/comodato", title: "Comodato impianti e spillatori | Guatemalan Spirit",
    description: "Spillatori inox per fusti Cornelius 19L e granitori professionali in comodato d'uso per bar, lidi e ristoranti: canone zero legato ai consumi, assistenza inclusa.",
    jsonLd: [{ "@context": "https://schema.org", "@type": "Service", name: "Comodato d'uso impianti per cocktail in fusto e granite", provider: { "@type": "Organization", name: "Guatemalan Spirits & Fruit Juice — BOTTY srl" }, areaServed: "Campania", serviceType: "Comodato d'uso spillatori e granitori" }] });
  return (
    <div className="paper-grain min-h-screen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20" data-testid="comodato-page">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={fadeUp} initial="hidden" animate="show">
            <div className="eyebrow text-[#C2593F] mb-3">{c.eyebrow}</div>
            <h1 className="font-serif font-semibold text-4xl sm:text-5xl text-[#0B2A45] mb-5 leading-tight">{c.title}</h1>
            <p className="text-stone-600 leading-relaxed mb-6">{c.text}</p>
            <ul className="space-y-3 mb-8">{(c.bullets || []).map((t) => <li key={t} className="flex items-center gap-3 text-sm text-stone-700"><span className="w-2 h-2 rounded-full bg-[#EAB308]" /> {t}</li>)}</ul>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="bg-[#C2593F] hover:bg-[#9A3412] text-white rounded-full px-7 h-12"><Link to={target} data-testid="comodato-cta">Richiedi un impianto <ArrowRight className="w-4 h-4 ml-2" /></Link></Button>
              <Button asChild variant="outline" className="rounded-full px-7 h-12 border-stone-300 hover:bg-[#FEF9C3]"><Link to="/listino" data-testid="comodato-listino-link">Vedi il listino fusti</Link></Button>
            </div>
          </motion.div>
          <div className="grid grid-cols-2 gap-4">
            <img src={c.image1} alt="Spillatore inox" className="rounded-2xl h-64 w-full object-cover border-4 border-white shadow-lg" />
            <img src={c.image2} alt="Produzione artigianale" className="rounded-2xl h-64 w-full object-cover border-4 border-white shadow-lg mt-8" />
          </div>
        </div>

        <div className="mt-20" data-testid="comodato-benefits">
          <div className="text-center mb-10">
            <div className="eyebrow text-[#C2593F] mb-3">Perché il cocktail in fusto</div>
            <h2 className="font-serif font-semibold text-2xl lg:text-3xl text-[#0B2A45]">Qualità costante, servizio più veloce</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => (
              <motion.div key={b.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={VIEWPORT} className="bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-testid={`comodato-benefit-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#FEF3C7] flex items-center justify-center mb-4"><b.icon className="w-5 h-5 text-[#C2593F]" /></div>
                <h3 className="font-serif text-lg font-semibold text-[#0B2A45] mb-2">{b.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <Machines machines={machines} target={target} />
        <SopralluogoForm />
      </section>
      <Footer />
    </div>
  );
}
